import React from 'react'
import { GameBoard } from '@prometheus/types'
import { Card, Cards, Popup, Stack, Typography } from '@prometheus/components'
import PromiseButtonV2 from '@/components/PromiseButtonV2'
import usePlayerActions from '../../hooks/usePlayerActions'
import { useSelector } from '../../../../hooks/useSelector'
import formatCost from '../../../../utils/formatCost'

interface TileUpgradesPopupProps {
  planet: keyof GameBoard
  trigger: React.ReactNode
  position?: string
}

// The tile-upgrades panel (Enhance/Downgrade + cost ladder), shared between the
// full planet card's tile button and the compact card's tile chip. It sits on
// its own control: buying a faster tile has nothing to do with how far through
// its cycle the planet currently is, and pairing them made the cycle readout
// look like it was describing the tile price.
export const TileUpgradesPopup = ({ planet, trigger, position = 'right center' }: TileUpgradesPopupProps) => {
  const { onUpgradePlanet } = usePlayerActions()
  const tile_upgrades = useSelector(s => s.game.metadata?.tile_upgrades) || []
  const disable_planet_tokens = !!useSelector(s => s.game.options?.disable_planet_tokens)

  return (
    <Popup position={position as any} hoverable trigger={trigger}>
      <Card padding="sm">
        <Stack>
          <Typography variant="h4" retro>Tile Upgrades</Typography>
          <Typography variant="body2">
            A faster tile shortens the planet&apos;s cycle, so it mines and levels more often.
            This is a separate purchase from whatever the next level costs.
          </Typography>
          <PromiseButtonV2 onClick={() => onUpgradePlanet(planet, 'tile')} variant="primary" ignoreGameLogic>
            Enhance Tile
          </PromiseButtonV2>
          <Typography>{disable_planet_tokens ? 'Increase upgrade speed of planet' : 'Upgrade closer to 7'}</Typography>
          {disable_planet_tokens ? '' : (
            <PromiseButtonV2 onClick={() => onUpgradePlanet(planet, 'downgrade_tile')} variant="warning" ignoreGameLogic>
              Downgrade Tile
            </PromiseButtonV2>
          )}
          <Cards>
            {tile_upgrades.slice(0, tile_upgrades.length - 1).map((upgrade, i) => {
              return (
                <Card padding="sm" key={upgrade.from_numbers.join(', ')}>
                  <Typography>
                    {upgrade.from_numbers.join(' or ')} =&gt;&nbsp;
                    {formatCost(upgrade.cost) + ' => '}
                    {tile_upgrades[i + 1].from_numbers.join(' or ')}
                  </Typography>
                </Card>
              )
            })}
          </Cards>
        </Stack>
      </Card>
    </Popup>
  )
}

export default TileUpgradesPopup
